import {
  breakpointSizes,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React from 'react'
import { useSelector } from 'react-redux'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import Buildings from 'src/assets/images/Buildings.svg'
import APIProductEmptyStatePending from 'src/assets/svgs/APIProductEmptyStatePending.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import StatusLabel from 'src/components/InfoPill/InfoPill'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import TestId from 'src/components/TestId/TestId'
import { ImageNames } from 'src/enums'
import { OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  addExpenseUrl,
  createBudgetUrl,
  createVirtualCardUrl,
  departmentsUrl,
  registerAccountUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsSetupOrganizationPopupOpen,
} from 'src/redux/actions/app.actions'
import { LocationState, RootState } from 'src/redux/createRootReducer'
import { selectIsUserOrglessRCNOrBudgetOwner } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

export enum TableEmptyStateDesigns {
  YourVirtualCardsActive,
  YourVirtualCardsInactive,
  AccountTransactions,
  VirtualCardTransactions,
  RecentTransactions,
  PendingRequests,
  YourCardsTransactions,
  AccountsAccounts,
  AccountVirtualCardsActive,
  AccountVirtualCardsInactive,
  SearchVirtualCardsUnfiltered,
  SearchVirtualCardsFiltered,
  AccountBudgets,
  AccountBudgetsFiltered,
  VirtualCardsFiltered,
  NoBusinessRules,
  ApproverExpenseReports,
  SubmitterExpenseReports,
  SubmitterReportDetail,
  ApproverReportDetail,
  UserIsViewingTheirReport,
  EmptyFiltersExpenseReports,
  PendingBudgets,
  BudgetRequests,
  OrgExpenseReportCycleSettings,
  BillPay,
  BillPayFiltered,
  ExpenseCategories,
  DepartmentsEnglish,
  DepartmentsFrench,
  ExpenseCategoryLabels,
  APIProduct,
  APIProductPending,
  APIProductEmptyAPIKeys,
  APIProductDenied,
  ExpensesNeedsApproval,
}

const getTitle = (
  design: TableEmptyStateDesigns,
): React.ReactElement | undefined => {
  switch (design) {
    case TableEmptyStateDesigns.SearchVirtualCardsUnfiltered: {
      return <Trns source={"Looks like you don't have any cards yet."} />
    }

    case TableEmptyStateDesigns.AccountVirtualCardsActive: {
      return <Trns source={'No cards have been created yet'} />
    }

    case TableEmptyStateDesigns.BillPay: {
      return <Trns source={'No Invoices Paid Yet'} />
    }

    case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
      return <Trns source={"You don't have an account set up!"} />
    }

    case TableEmptyStateDesigns.YourVirtualCardsActive: {
      return <Trns source={'Looks like you need a card to spend'} />
    }

    case TableEmptyStateDesigns.AccountVirtualCardsInactive:
    case TableEmptyStateDesigns.YourVirtualCardsInactive: {
      return <Trns source={'Nothing here'} />
    }

    case TableEmptyStateDesigns.YourCardsTransactions:
    case TableEmptyStateDesigns.VirtualCardTransactions:
    case TableEmptyStateDesigns.AccountTransactions: {
      return <Trns source={'Not a penny spent'} />
    }

    case TableEmptyStateDesigns.RecentTransactions: {
      return <Trns source={'There are no transactions in the last 30 days.'} />
    }

    case TableEmptyStateDesigns.PendingRequests: {
      return <Trns source={'You have no pending requests.'} />
    }

    case TableEmptyStateDesigns.AccountBudgetsFiltered: {
      return <Trns source={'No Budgets match your search.'} />
    }

    case TableEmptyStateDesigns.AccountBudgets: {
      return (
        <TestId testId="noActiveBudgets">
          <Trns source={'No active budgets'} />
        </TestId>
      )
    }

    case TableEmptyStateDesigns.VirtualCardsFiltered: {
      return <Trns source={'No Virtual Cards match your search'} />
    }
    case TableEmptyStateDesigns.BillPayFiltered: {
      return <Trns source={'No Bill Pay Virtual Cards match your search'} />
    }
    case TableEmptyStateDesigns.NoBusinessRules: {
      return (
        <Trns source={'You currently don’t have access to Business Rules'} />
      )
    }

    case TableEmptyStateDesigns.ApproverExpenseReports: {
      return <Trns source={'No one has submitted reimbursement reports'} />
    }
    case TableEmptyStateDesigns.SubmitterExpenseReports: {
      return <Trns source={'You don’t have any reimbursement reports'} />
    }
    case TableEmptyStateDesigns.SubmitterReportDetail: {
      return <Trns source={'You don’t have any expenses in this report'} />
    }
    case TableEmptyStateDesigns.ApproverReportDetail: {
      return <Trns source={'There are no expenses in this report'} />
    }
    case TableEmptyStateDesigns.UserIsViewingTheirReport: {
      return <Trns source={'You don’t have any expenses in this report'} />
    }

    case TableEmptyStateDesigns.EmptyFiltersExpenseReports: {
      return (
        <Trns
          source={
            'You don’t have any reimbursement reports that meet your search criteria'
          }
        />
      )
    }

    case TableEmptyStateDesigns.AccountsAccounts: {
      return <Trns source={'No Accounts match your search.'} />
    }

    case TableEmptyStateDesigns.BudgetRequests: {
      return <Trns source={'No requests'} />
    }

    case TableEmptyStateDesigns.PendingBudgets: {
      return <Trns source={'No pending budgets'} />
    }

    case TableEmptyStateDesigns.ExpenseCategories: {
      return <Trns source={'No Expense Categories match your search'} />
    }

    case TableEmptyStateDesigns.ExpenseCategoryLabels: {
      return <Trns source={'No Expense Labels match your search'} />
    }

    case TableEmptyStateDesigns.DepartmentsEnglish:
    case TableEmptyStateDesigns.DepartmentsFrench: {
      return <Trns source={'Create teams for your organization'} />
    }

    case TableEmptyStateDesigns.APIProductPending: {
      return (
        <div>
          <Trns source="Set up API Keys" />
          <StatusLabel text={'Pending'} statusType={'INFO'} />
        </div>
      )
    }
    case TableEmptyStateDesigns.APIProduct: {
      return <Trns source={'Set up API Keys'} />
    }
    case TableEmptyStateDesigns.APIProductEmptyAPIKeys: {
      return <Trns source={'Generate API Key'} />
    }
    case TableEmptyStateDesigns.APIProductDenied: {
      return <Trns source={'Request Denied'} />
    }

    case TableEmptyStateDesigns.ExpensesNeedsApproval: {
      return <Trns source={"You're up to date!"} />
    }
  }
}

const getSecondaryMessages = (
  design: TableEmptyStateDesigns,
  isGuestUser: boolean,
  hasVirtualCardFeatures: boolean,
): React.ReactElement[] | undefined => {
  switch (design) {
    case TableEmptyStateDesigns.YourVirtualCardsActive: {
      return [
        <Trns
          key={0}
          source={
            "Request a card from an account holder or send one to yourself if that's you."
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.YourVirtualCardsInactive: {
      return [
        <Trns
          key={0}
          source={
            "This is where you'll see all your cards once they're expired or deactivated."
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.AccountVirtualCardsActive: {
      return [
        <Trns
          key={0}
          source={
            "Here is where you'll view or manage all the virtual cards within your account."
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
      return [
        <Trns
          key={0}
          source={
            'Register your company credit card account so you can start using Automated Expense Reports.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.AccountVirtualCardsInactive: {
      return [
        <Trns
          key={0}
          source={
            "This is where you'll see all the cards that have expired or have been deactivated."
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.YourCardsTransactions:
    case TableEmptyStateDesigns.VirtualCardTransactions:
    case TableEmptyStateDesigns.AccountTransactions: {
      return [
        <Trns key={0} source={'There are no transactions to show yet.'} />,
      ]
    }

    case TableEmptyStateDesigns.RecentTransactions: {
      if (isGuestUser || !hasVirtualCardFeatures) {
        return undefined
      }
      return [
        <Trns
          key={0}
          source={
            'You can use a virtual card like any other business credit card. Request a virtual card to pay for an expense, like airfare or a software subscription.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.AccountBudgets: {
      return [
        <Trns
          key={0}
          source={'Allocate budgets to others and manage them all here.'}
        />,
      ]
    }

    case TableEmptyStateDesigns.AccountBudgetsFiltered:
    case TableEmptyStateDesigns.VirtualCardsFiltered:
    case TableEmptyStateDesigns.BillPayFiltered:
    case TableEmptyStateDesigns.ExpenseCategories:
    case TableEmptyStateDesigns.ExpenseCategoryLabels: {
      return [
        <Trns
          key={0}
          source={
            'Check that your search is correct. You can also try broadening your search.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.NoBusinessRules: {
      return [
        <Trns
          key={0}
          source={
            'In the near future, your Organization will have access to more enhanced features to support your growing business needs.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.ApproverExpenseReports: {
      return [<Trns key={0} source={'Submitted reports will appear here.'} />]
    }
    case TableEmptyStateDesigns.SubmitterExpenseReports: {
      return [
        <Trns
          key={0}
          source={
            'You can create your first reimbursement report in the ‘Create New’ dropdown menu or click on the button below.'
          }
        />,
      ]
    }
    case TableEmptyStateDesigns.SubmitterReportDetail: {
      return [
        <Trns
          key={0}
          source={
            'You can add expenses to this report by clicking ‘+ Add Expense’ in the top right or down below.'
          }
        />,
      ]
    }
    case TableEmptyStateDesigns.ApproverReportDetail: {
      return [
        <Trns
          key={0}
          source={
            'Looks like the submitter of this report didn’t add any expenses to it.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.AccountsAccounts: {
      return [
        <Trns
          key={0}
          source={
            'Check that your search is correct. You can also try broadening your search.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.BudgetRequests: {
      return [<Trns key={0} source={'You have no requests'} />]
    }

    case TableEmptyStateDesigns.PendingBudgets: {
      return [<Trns key={0} source={'You have no pending budgets'} />]
    }

    case TableEmptyStateDesigns.BillPay: {
      return [
        <Trns
          key={0}
          source={
            'Want to pay an invoice? Create a virtual card to pay your invoices.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.DepartmentsEnglish: {
      return [
        <Trns
          key={0}
          source={
            'There are no teams set up for your organization. Click "Add Team" to create one. You will be able to add members of your organization to a team once it has been created.'
          }
        />,
        <Trns
          key={1}
          source={'Learn more about Teams in the ${resourceCenter}.'}
          expressions={{
            resourceCenter: (
              <ResourceCenterLink
                size={HoverableLinkSizes.Large}
                underline
                regular
                analytics={{
                  event:
                    'departments - departments helpcenter link - text link press',
                  properties: {
                    interaction: 'text link press',
                    intent: 'departments helpcenter link',
                    screen: departmentsUrl(':id'),
                  },
                }}
              />
            ),
          }}
        />,
      ]
    }
    case TableEmptyStateDesigns.DepartmentsFrench: {
      return [
        <Trns
          key={0}
          source={
            'There are no teams set up for your organization. Click "Add Team" to create one. You will be able to add members of your organization to a team once it has been created.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.APIProductPending: {
      return [
        <Trns
          key={0}
          source={
            'A member of our team is currently reviewing your request and will respond as soon as possible.'
          }
        />,
      ]
    }
    case TableEmptyStateDesigns.APIProductEmptyAPIKeys:
      return [
        <Trns
          key={0}
          source={
            'Your request to obtain an API key has been approved. Click “Generate API Keys” to create your API key and secret.'
          }
        />,
      ]
    case TableEmptyStateDesigns.APIProduct: {
      return [
        <Trns
          key={0}
          source={
            'Looks like you have no API Keys. Click the button below to get started.'
          }
        />,
      ]
    }
    case TableEmptyStateDesigns.APIProductDenied: {
      return [
        <Trns
          key={0}
          source={
            'Your request to obtain an API key has been denied. Please contact support for more information.'
          }
        />,
      ]
    }

    case TableEmptyStateDesigns.ExpensesNeedsApproval: {
      return [
        <Trns
          key={0}
          source={"We'll notify you when new items need review."}
        />,
      ]
    }
  }
}

export type TableEmptyStateActionButton =
  | TableEmptyStateActionButtonAriaSource
  | TableEmptyStateActionButtonAriaLabel

interface TableEmptyStateActionButtonShared {
  text: React.ReactElement
  to?: string | Location<LocationState>
  onClick?: React.MouseEventHandler
}

interface TableEmptyStateActionButtonAriaSource
  extends TableEmptyStateActionButtonShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface TableEmptyStateActionButtonAriaLabel
  extends TableEmptyStateActionButtonShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

interface TableEmptyStateProps {
  className?: string
  design: TableEmptyStateDesigns
  title?: React.ReactElement
  readOnly?: boolean
  wide?: boolean
  expenseReportId?: string
  isLink?: boolean
  buttonClickAction?: () => void
}

const TableEmptyState: React.FC<
  React.PropsWithChildren<TableEmptyStateProps>
> = (props): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const { data: user } = useQueryOwnUser({})
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}
  const title = props.title || getTitle(props.design)
  const secondaryMessages = getSecondaryMessages(
    props.design,
    !!isGuestUser,
    !!hasVirtualCardFeatures,
  )
  const wide = props.wide
  const submitterExpenseReports =
    props.design === TableEmptyStateDesigns.SubmitterExpenseReports
  const isExpenses =
    props.design === TableEmptyStateDesigns.ApproverExpenseReports ||
    submitterExpenseReports ||
    props.design === TableEmptyStateDesigns.SubmitterReportDetail ||
    TableEmptyStateDesigns.ApproverReportDetail
  const isUserOrglessRCNOrBudgetOwner = useSelector(
    selectIsUserOrglessRCNOrBudgetOwner,
  )

  const getImageSrc = (design: TableEmptyStateDesigns): string => {
    switch (design) {
      case TableEmptyStateDesigns.AccountVirtualCardsActive:
      case TableEmptyStateDesigns.AccountVirtualCardsInactive:
      case TableEmptyStateDesigns.PendingRequests:
      case TableEmptyStateDesigns.SearchVirtualCardsFiltered:
      case TableEmptyStateDesigns.SearchVirtualCardsUnfiltered:
      case TableEmptyStateDesigns.YourVirtualCardsActive:
      case TableEmptyStateDesigns.YourVirtualCardsInactive:
      case TableEmptyStateDesigns.VirtualCardsFiltered:
      case TableEmptyStateDesigns.BillPay:
      case TableEmptyStateDesigns.BillPayFiltered: {
        return useColorOrGrayscaleImage(ImageNames.NoVirtualCards)
      }

      case TableEmptyStateDesigns.YourCardsTransactions:
      case TableEmptyStateDesigns.VirtualCardTransactions:
      case TableEmptyStateDesigns.RecentTransactions:
      case TableEmptyStateDesigns.AccountTransactions: {
        return useColorOrGrayscaleImage(ImageNames.NoTransactions)
      }

      case TableEmptyStateDesigns.PendingBudgets:
      case TableEmptyStateDesigns.BudgetRequests:
      case TableEmptyStateDesigns.AccountBudgetsFiltered:
      case TableEmptyStateDesigns.AccountBudgets: {
        return useColorOrGrayscaleImage(ImageNames.NoBudgets)
      }

      case TableEmptyStateDesigns.NoBusinessRules: {
        return Buildings
      }

      case TableEmptyStateDesigns.EmptyFiltersExpenseReports:
      case TableEmptyStateDesigns.UserIsViewingTheirReport:
      case TableEmptyStateDesigns.ApproverReportDetail:
      case TableEmptyStateDesigns.SubmitterReportDetail:
      case TableEmptyStateDesigns.SubmitterExpenseReports:
      case TableEmptyStateDesigns.ApproverExpenseReports: {
        return useColorOrGrayscaleImage(ImageNames.NoOOPE)
      }

      case TableEmptyStateDesigns.AccountsAccounts:
      case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
        return useColorOrGrayscaleImage(ImageNames.AccountsEmptyState)
      }

      case TableEmptyStateDesigns.ExpenseCategories:
      case TableEmptyStateDesigns.ExpenseCategoryLabels: {
        return useColorOrGrayscaleImage(ImageNames.NoSource)
      }

      case TableEmptyStateDesigns.DepartmentsEnglish:
      case TableEmptyStateDesigns.DepartmentsFrench: {
        return useColorOrGrayscaleImage(ImageNames.BuildingsPeople)
      }

      case TableEmptyStateDesigns.APIProductPending: {
        return APIProductEmptyStatePending
      }
      case TableEmptyStateDesigns.APIProductEmptyAPIKeys:
      case TableEmptyStateDesigns.APIProductDenied:
      case TableEmptyStateDesigns.APIProduct: {
        return useColorOrGrayscaleImage(ImageNames.APIProductEmptyState)
      }

      case TableEmptyStateDesigns.ExpensesNeedsApproval: {
        return useColorOrGrayscaleImage(ImageNames.NothingToReview)
      }
    }
  }
  const imageSrc = getImageSrc(props.design)

  const handleCreateBudgetClick = (): void => {
    if (isUserOrglessRCNOrBudgetOwner) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true, createBudgetUrl()))
    }
  }

  const getActionButton = (
    design: TableEmptyStateDesigns,
    dispatch: ThunkDispatch<RootState, void, AnyAction>,
    expenseReportId: string | undefined,
    isGuestUser: boolean,
    hasVirtualCardFeatures: boolean,
  ): TableEmptyStateActionButton | undefined => {
    switch (design) {
      case TableEmptyStateDesigns.RecentTransactions:
      case TableEmptyStateDesigns.YourVirtualCardsActive: {
        if (isGuestUser || !hasVirtualCardFeatures) {
          return undefined
        }
        return {
          text: <Trns source={'Request a Virtual Card'} />,
          to: requestVirtualCardUrl(),
          ariaLabelSource: 'Request a Virtual Card',
        }
      }
      case TableEmptyStateDesigns.AccountBudgets: {
        return {
          text: <Trns source={'Create a Budget'} />,
          onClick: handleCreateBudgetClick,
          to: isUserOrglessRCNOrBudgetOwner ? undefined : createBudgetUrl(),
          ariaLabelSource: 'Create a Budget',
        }
      }
      case TableEmptyStateDesigns.AccountVirtualCardsActive: {
        return {
          text: <Trns source={'Create a Virtual Card'} />,
          to: createVirtualCardUrl(),
          ariaLabelSource: 'Create a Virtual Card',
        }
      }
      case TableEmptyStateDesigns.BillPay: {
        return {
          text: <Trns source={'Create a Bill Pay Virtual Card'} />,
          to: {
            pathname: createVirtualCardUrl(),
            search: '',
            state: {
              screen: CreateVirtualCardScreen.BillPay,
            },
            hash: '',
          },
          ariaLabelSource: 'Create a Bill Pay Virtual Card',
        }
      }
      case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
        return {
          text: <Trns source={'Register Account'} />,
          to: registerAccountUrl(),
          ariaLabelSource: 'Register Account',
        }
      }
      case TableEmptyStateDesigns.SubmitterExpenseReports: {
        return {
          text: <Trns source={'Create Reimbursement Report'} />,
          onClick: (): void => {
            dispatch(createSetIsCreateExpenseReportPopupOpen(true))
          },
          ariaLabelSource: 'Create Reimbursement Report',
        }
      }
      case TableEmptyStateDesigns.SubmitterReportDetail: {
        if (expenseReportId !== undefined) {
          return {
            text: <Trns source={'+ Add Expense'} />,
            to: addExpenseUrl(expenseReportId),
            ariaLabelSource: 'Add Expense',
          }
        } else {
          return undefined
        }
      }
      case TableEmptyStateDesigns.DepartmentsEnglish:
      case TableEmptyStateDesigns.DepartmentsFrench: {
        // TODO: update URL when add depts modal is built out
        return {
          text: <Trns source={'+ Add Team'} />,
          // to: '/departments/add',
          onClick: () => props.buttonClickAction && props.buttonClickAction(),
          ariaLabelSource: 'Add Team',
        }
      }

      case TableEmptyStateDesigns.APIProduct: {
        return {
          text: <Trns source={'Set up API Keys'} />,
          onClick: () => props.buttonClickAction && props.buttonClickAction(),
          ariaLabelSource: 'Set up API Keys',
        }
      }

      case TableEmptyStateDesigns.APIProductDenied:
      case TableEmptyStateDesigns.APIProductPending: {
        return undefined
      }
      case TableEmptyStateDesigns.APIProductEmptyAPIKeys: {
        return {
          text: <Trns source={'Generate API Key'} />,
          onClick: () => props.buttonClickAction && props.buttonClickAction(),
          ariaLabelSource: 'Generate API Key',
        }
      }
      default: {
        return undefined
      }
    }
  }

  const actionButton = getActionButton(
    props.design,
    dispatch,
    props.expenseReportId,
    !!isGuestUser,
    !!hasVirtualCardFeatures,
  )

  return (
    <div
      className={props.className}
      css={css`
        width: 100%;
        display: flex;
        justify-content: center;
      `}
      data-testid={'emptyTable'}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: ${wide ? `558px` : `380px`};
          ${getBreakpointUp(breakpointSizes.XSM)(css`
            padding: 36px 0px;
          `)}
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: 36px 20px;
          `)}
        `}
      >
        <img src={imageSrc} alt={''} />
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
            text-align: center;
            margin-top: 30px;
            ${isExpenses &&
            css`
              margin-top: 10px;
            `}
          `}
        >
          {title}
        </span>
        {secondaryMessages?.map((message, i) => (
          <span
            key={i}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
              text-align: center;
              margin-top: 20px;
              ${submitterExpenseReports &&
              css`
                max-width: 456px;
              `}
              ${props.design === TableEmptyStateDesigns.SubmitterReportDetail &&
              css`
                max-width: 350px;
              `}
            `}
          >
            {message}
          </span>
        ))}
        {actionButton !== undefined &&
          !props.readOnly &&
          (props.isLink ? (
            <HoverableLink
              css={css`
                margin-top: 30px;
              `}
              ariaLabelSource={
                (actionButton as TableEmptyStateActionButtonAriaSource)
                  .ariaLabelSource
              }
              to={actionButton.to}
              testId={'emptyTableLink'}
            >
              {actionButton.text}
            </HoverableLink>
          ) : (
            <Button
              linkProps={
                actionButton.to !== undefined
                  ? {
                      to: actionButton.to,
                      ...(actionButton.ariaLabelSource !== undefined
                        ? { ariaLabelSource: actionButton.ariaLabelSource }
                        : { ariaLabel: actionButton.ariaLabel }),
                    }
                  : undefined
              }
              className={'action-button'}
              css={css`
                margin-top: 30px;
              `}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.button}
              text={actionButton.text}
              onClick={
                actionButton.onClick !== undefined
                  ? actionButton.onClick
                  : undefined
              }
              big={submitterExpenseReports}
              testId={'emptyTableBtn'}
            />
          ))}
      </div>
    </div>
  )
}

export default TableEmptyState
